import { Package, ChevronRight, ArrowLeft } from 'lucide-react'
import { cn } from "@/lib/utils"
import { Button } from '@/components/ui/button'
import { IdResolutionHint } from './IdResolutionHint'

interface Variant {
    id: string
    name: string
    sku: string | null
    imageUrl: string | null
    prices: any[]
}

interface VariantDropdownProps {
    variants: Variant[]
    onSelect: (variantId: string) => void
    onBack: () => void
    showAbove?: boolean
    /** Explains how a pasted id got the picker to this step. */
    notice?: string | null
}

const DropdownHeader = ({
    title,
    onBack,
    notice,
}: {
    title: string
    onBack: () => void
    notice?: string | null
}) => {
    return (
        <div className="sticky top-0 bg-background border-b px-4 py-3 z-10">
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        onBack()
                    }}
                    className="h-8 w-8 p-0"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            </div>
            {notice && <IdResolutionHint message={notice} className="mt-2" />}
        </div>
    )
}

export const VariantDropdown = ({ variants, onSelect, onBack, showAbove, notice }: VariantDropdownProps) => {
    return (
        <div className={cn(
            "absolute z-[10001] w-full bg-background border rounded-lg shadow-lg max-h-[300px] overflow-hidden flex flex-col",
            showAbove ? "bottom-full mb-1" : "top-full mt-1"
        )}>
            <DropdownHeader title="Select a Variant" onBack={onBack} notice={notice} />
            <div className="overflow-y-auto">
                {variants.map((variant) => (
                    <div
                        key={variant.id}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            onSelect(variant.id)
                        }}
                        className={cn(
                            'flex items-center gap-3 p-3 cursor-pointer transition-colors',
                            'hover:bg-accent border-b last:border-b-0',
                        )}
                    >
                        <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-md overflow-hidden flex items-center justify-center">
                            {variant.imageUrl ? (
                                <img
                                    src={variant.imageUrl}
                                    alt={variant.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <Package className="w-6 h-6 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{variant.name}</p>
                            {variant.sku && (
                                <p className="text-sm text-muted-foreground truncate">SKU: {variant.sku}</p>
                            )}
                        </div>
                        <div className="flex-shrink-0 text-sm text-muted-foreground">
                            {variant.prices.length} {variant.prices.length === 1 ? 'price' : 'prices'}
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                ))}
            </div>
        </div>
    )
}

